import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tooltip,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { LeaveRequest, LeaveRequestStatus } from '../types';
import { isWeekend, toLocalDateStr } from '../utils/hkHolidays';
import { apiService } from '../services/api';

interface LeaveCalendarProps {
  open: boolean;
  onClose: () => void;
  leaveRequests: LeaveRequest[];
  employeeCountry?: string;
}

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const LeaveCalendar: React.FC<LeaveCalendarProps> = ({ open, onClose, leaveRequests, employeeCountry }) => {
  const [currentDate, setCurrentDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const today = toLocalDateStr(new Date());

  // Holiday map: date string → holiday name (from public_holidays table)
  const [dbHolidayMap, setDbHolidayMap] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    if (!open) return;
    const years = [currentDate.getFullYear()];
    // Also fetch next year if we're in the last 2 months of the year
    if (currentDate.getMonth() >= 10) years.push(currentDate.getFullYear() + 1);

    const fetchHolidays = async () => {
      // Resolve employee's country to a countryId (non-fatal if it fails)
      let countryId: number | undefined;
      if (employeeCountry) {
        try {
          const countries = await apiService.getCountries();
          const match = countries.find(
            (c: any) =>
              c.name.toLowerCase() === employeeCountry.toLowerCase() ||
              c.code.toLowerCase() === employeeCountry.toLowerCase()
          );
          if (match) countryId = match.id;
        } catch {
          // proceed without country filter
        }
      }

      try {
        const results = await Promise.all(
          years.map(y => apiService.getPublicHolidays(y, countryId))
        );
        const map = new Map<string, string>();
        results.flat().forEach((h: any) => {
          const dateStr = String(h.date).split('T')[0];
          map.set(dateStr, h.name);
        });
        setDbHolidayMap(map);
      } catch {
        // holiday fetch failed
      }
    };

    fetchHolidays();
  }, [open, currentDate.getFullYear(), employeeCountry]);

  const getHolidayName = (dateStr: string): string | undefined =>
    dbHolidayMap.get(dateStr);

  const prevMonth = () =>
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));

  const nextMonth = () =>
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const monthLabel = currentDate.toLocaleDateString('en-HK', { month: 'long', year: 'numeric' });

  const getLeaveRequestsForDay = (dateStr: string): LeaveRequest[] =>
    leaveRequests.filter(req => {
      const start = req.startDate.split('T')[0];
      const end = req.endDate.split('T')[0];
      return (
        dateStr >= start &&
        dateStr <= end &&
        req.status !== LeaveRequestStatus.CANCELLED &&
        req.status !== LeaveRequestStatus.REJECTED
      );
    });

  // Build flat cell array: null = empty padding, Date = actual day
  const cells: (Date | null)[] = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => new Date(year, month, i + 1)),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Leave Calendar</Typography>
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Month navigation */}
        <Box display="flex" justifyContent="center" alignItems="center" mb={2}>
          <IconButton onClick={prevMonth}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" sx={{ mx: 2, minWidth: 200, textAlign: 'center' }}>
            {monthLabel}
          </Typography>
          <IconButton onClick={nextMonth}>
            <ChevronRight />
          </IconButton>
        </Box>

        {/* Day-of-week headers */}
        <Box
          display="grid"
          sx={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', mb: '2px' }}
        >
          {DAY_HEADERS.map(day => (
            <Box
              key={day}
              sx={{
                textAlign: 'center',
                py: 0.75,
                bgcolor: day === 'Sun' || day === 'Sat' ? '#e0e0e0' : '#f5f5f5',
                borderRadius: '4px 4px 0 0',
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ color: day === 'Sun' ? '#d32f2f' : day === 'Sat' ? 'text.secondary' : 'text.primary' }}
              >
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar grid */}
        <Box display="grid" sx={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
          {cells.map((date, idx) => {
            if (!date) {
              return (
                <Box
                  key={`empty-${idx}`}
                  sx={{ minHeight: 80, bgcolor: '#fafafa', borderRadius: 1 }}
                />
              );
            }

            const dateStr = toLocalDateStr(date);
            const isToday = dateStr === today;
            const weekend = isWeekend(date);
            const holidayName = getHolidayName(dateStr);
            const dayLeaves = getLeaveRequestsForDay(dateStr);

            let bgcolor = '#ffffff';
            if (weekend) bgcolor = '#f5f5f5';
            if (holidayName) bgcolor = '#fff3e0';
            if (isToday) bgcolor = '#e3f2fd';

            const border = isToday
              ? '2px solid #1976d2'
              : holidayName
              ? '1px solid #ff9800'
              : '1px solid #e0e0e0';

            const tooltipLines = [
              holidayName,
              ...dayLeaves.map(
                r => `${r.leaveType?.name || 'Leave'} (${r.status})`
              ),
            ].filter(Boolean);

            const cell = (
              <Box
                sx={{
                  minHeight: 80,
                  p: '6px',
                  bgcolor,
                  border,
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  cursor: tooltipLines.length ? 'default' : 'default',
                }}
              >
                {/* Day number */}
                <Typography
                  variant="caption"
                  fontWeight={isToday ? 'bold' : 'normal'}
                  sx={{
                    lineHeight: 1,
                    mb: '2px',
                    color: holidayName || date.getDay() === 0 ? '#d32f2f' : date.getDay() === 6 ? 'text.secondary' : 'text.primary',
                  }}
                >
                  {date.getDate()}
                </Typography>

                {/* Holiday label */}
                {holidayName && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '9px',
                      color: '#d32f2f',
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {holidayName}
                  </Typography>
                )}

                {/* Leave dots */}
                <Box display="flex" flexWrap="wrap" gap="3px" mt="auto">
                  {dayLeaves.map((req, i) => (
                    <Box
                      key={`${req.id}-${i}`}
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor:
                          req.status === LeaveRequestStatus.APPROVED
                            ? '#4caf50'
                            : '#ff9800',
                        flexShrink: 0,
                      }}
                    />
                  ))}
                </Box>
              </Box>
            );

            return tooltipLines.length > 0 ? (
              <Tooltip
                key={dateStr}
                title={
                  <Box>
                    {tooltipLines.map((line, i) => (
                      <Typography key={i} variant="caption" display="block">
                        {line}
                      </Typography>
                    ))}
                  </Box>
                }
                arrow
              >
                {cell}
              </Tooltip>
            ) : (
              <React.Fragment key={dateStr}>{cell}</React.Fragment>
            );
          })}
        </Box>

        {/* Legend */}
        <Box display="flex" gap={3} mt={2} flexWrap="wrap" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: '#fff3e0',
                border: '1px solid #ff9800',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Public Holiday</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: '#e3f2fd',
                border: '2px solid #1976d2',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Today</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: '#4caf50',
                borderRadius: '50%',
              }}
            />
            <Typography variant="caption">Approved Leave</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 10,
                height: 10,
                bgcolor: '#ff9800',
                borderRadius: '50%',
              }}
            />
            <Typography variant="caption">Pending Leave</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: '#f5f5f5',
                border: '1px solid #e0e0e0',
                borderRadius: 0.5,
              }}
            />
            <Typography variant="caption">Weekend</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveCalendar;
